import React from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom'
import { App as AntdApp, ConfigProvider } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { Toaster } from 'sonner'
import Layout from './components/Layout'
import Home from './pages/Home'
import './App.css'
import { request } from './utils/request'
import { FontScaleProvider, useFontScale } from './contexts/FontScaleContext'

type AppErrorBoundaryState = { hasError: boolean; errorMessage: string; isRecovering: boolean }

const CHUNK_RECOVERY_STORAGE_KEY = 'ebookhub:chunk-reload:v1'
const CHUNK_RECOVERY_COOLDOWN_MS = 30000
const RECOVERABLE_CHUNK_ERROR_RE =
  /(Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS)/i

/**
 * 规范化懒加载错误信息，统一用于自动恢复判断。
 * @param error 运行时错误对象
 * @returns 可读的错误文本
 */
const normalizeChunkErrorMessage = (error: unknown): string => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; reason?: unknown }
    if (typeof candidate.message === 'string') return candidate.message
    if (typeof candidate.reason === 'string') return candidate.reason
    if (candidate.reason && typeof candidate.reason === 'object') {
      const nestedReason = candidate.reason as { message?: unknown }
      if (typeof nestedReason.message === 'string') return nestedReason.message
    }
  }
  return ''
}

/**
 * 对 Vite 懒加载 chunk 失效进行一次性自动刷新，解决旧入口脚本引用新旧 chunk 不一致的问题。
 * @param error 运行时错误对象
 * @returns 是否已触发自动恢复
 */
const scheduleChunkRecovery = (error: unknown): boolean => {
  const messageText = normalizeChunkErrorMessage(error)
  if (!RECOVERABLE_CHUNK_ERROR_RE.test(messageText)) return false

  try {
    if (typeof window === 'undefined') return false
    const routeKey = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const now = Date.now()
    const raw = sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY)
    if (raw) {
      const previous = JSON.parse(raw) as { routeKey?: unknown; at?: unknown }
      const previousRouteKey = typeof previous?.routeKey === 'string' ? previous.routeKey : ''
      const previousAt = Number(previous?.at || 0)
      if (previousRouteKey === routeKey && Number.isFinite(previousAt) && now - previousAt < CHUNK_RECOVERY_COOLDOWN_MS) {
        return false
      }
    }

    sessionStorage.setItem(
      CHUNK_RECOVERY_STORAGE_KEY,
      JSON.stringify({
        routeKey,
        at: now,
        reason: messageText.slice(0, 300),
      })
    )
    window.setTimeout(() => {
      try {
        window.location.reload()
      } catch {
        void 0
      }
    }, 80)
    return true
  } catch {
    return false
  }
}

class AppErrorBoundary extends React.PureComponent<{ children: React.ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false, errorMessage: '', isRecovering: false }

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    const msg = normalizeChunkErrorMessage(error)
    return { hasError: true, errorMessage: msg, isRecovering: false }
  }

  componentDidMount() {
    window.setTimeout(() => {
      try {
        const raw = sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY)
        if (!raw) return
        const cached = JSON.parse(raw) as { at?: unknown }
        const cachedAt = Number(cached?.at || 0)
        if (!Number.isFinite(cachedAt) || Date.now() - cachedAt >= 5000) {
          sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY)
        }
      } catch {
        void 0
      }
    }, 5000)
  }

  componentDidCatch(error: unknown) {
    try { console.error('App crashed:', error) } catch { void 0 }
    const isRecovering = scheduleChunkRecovery(error)
    if (isRecovering) {
      this.setState({ isRecovering: true })
    }
  }

  render() {
    if (this.state.hasError) {
      const isDev = String((import.meta as unknown as { env?: { MODE?: string } }).env?.MODE || '').toLowerCase() === 'development'
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ maxWidth: 520, width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>页面出现异常</div>
            <div style={{ color: '#666', marginBottom: 16 }}>
              {this.state.isRecovering ? '检测到页面资源已更新，正在自动刷新...' : '请刷新页面重试；如持续出现，建议返回首页再操作。'}
            </div>
            {isDev && this.state.errorMessage ? (
              <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 8, border: '1px solid #eee', marginBottom: 16 }}>
                {this.state.errorMessage}
              </pre>
            ) : null}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => { try { window.location.reload() } catch { void 0 } }}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #1677ff', background: '#1677ff', color: '#fff', cursor: 'pointer' }}
              >
                刷新页面
              </button>
              <button
                type="button"
                onClick={() => { try { window.location.href = '/' } catch { void 0 } }}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#333', cursor: 'pointer' }}
              >
                返回首页
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const Import = React.lazy(() => Promise.resolve().then(() => import('./pages/Import')))
const Search = React.lazy(() => Promise.resolve().then(() => import('./pages/Search')))
const Reader = React.lazy(() => Promise.resolve().then(() => import('./pages/Reader')))
const Admin = React.lazy(() => Promise.resolve().then(() => import('./pages/Admin')))
const Login = React.lazy(() => Promise.resolve().then(() => import('./pages/Login')))
const Favorites = React.lazy(() => Promise.resolve().then(() => import('./pages/Favorites')))
const Profile = React.lazy(() => Promise.resolve().then(() => import('./pages/Profile')))
const Help = React.lazy(() => Promise.resolve().then(() => import('./pages/Help')))
const TextToSpeech = React.lazy(() => Promise.resolve().then(() => import('./pages/TextToSpeech')))

const RouteTracker: React.FC = () => {
  const loc = useLocation()
  React.useEffect(() => {
    try {
      const p = String(loc.pathname || '/')
      const s = String(loc.search || '')
      const h = String(loc.hash || '')
      const full = p + s + h
      if (!/^\/reader\//.test(p) && !/^\/login\b/.test(p) && !/^\/previews\//.test(p)) {
        sessionStorage.setItem('last-route-before-reader', full)
        try {
          localStorage.setItem('last-route-before-reader-persist', JSON.stringify({ path: full, at: Date.now() }))
        } catch { void 0 }
      }
    } catch { /* noop */ }
  }, [loc.pathname, loc.search, loc.hash])
  return null
}

const ROUTE_SCROLL_STORAGE_PREFIX = 'ebookhub:route-scroll:v1:'
const EXCLUDED_SCROLL_RESTORE_ROUTE_RE = /^\/reader\b/

const buildRouteScrollStorageKey = (pathname: string, search: string, hash: string) => {
  const routeKey = `${String(pathname || '/')}${String(search || '')}${String(hash || '')}`
  return `${ROUTE_SCROLL_STORAGE_PREFIX}${routeKey}`
}

const readRouteScrollPosition = (storageKey: string) => {
  try {
    const raw = sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { x?: unknown; y?: unknown }
    const x = Math.max(0, Math.floor(Number(parsed?.x || 0)))
    const y = Math.max(0, Math.floor(Number(parsed?.y || 0)))
    return { x, y }
  } catch {
    return null
  }
}

const writeRouteScrollPosition = (storageKey: string) => {
  try {
    const payload = JSON.stringify({
      x: Math.max(0, Math.floor(window.scrollX || 0)),
      y: Math.max(0, Math.floor(window.scrollY || 0)),
      at: Date.now()
    })
    try { sessionStorage.setItem(storageKey, payload) } catch { void 0 }
    try { localStorage.setItem(storageKey, payload) } catch { void 0 }
  } catch {
    void 0
  }
}

const RouteScrollMemory: React.FC = () => {
  const loc = useLocation()
  const navigationType = useNavigationType()
  const restoreRafRef = React.useRef<number | null>(null)
  const previousPathnameRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const historyRef = window.history as History & { scrollRestoration?: 'auto' | 'manual' }
    const prev = historyRef.scrollRestoration
    try { historyRef.scrollRestoration = 'manual' } catch { void 0 }
    return () => {
      try {
        if (prev) historyRef.scrollRestoration = prev
      } catch { void 0 }
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const storageKey = buildRouteScrollStorageKey(loc.pathname, loc.search, loc.hash)
    if (EXCLUDED_SCROLL_RESTORE_ROUTE_RE.test(loc.pathname)) return

    const persist = () => writeRouteScrollPosition(storageKey)
    let throttleTimer: number | null = null
    const schedulePersist = () => {
      if (throttleTimer != null) return
      throttleTimer = window.setTimeout(() => {
        throttleTimer = null
        persist()
      }, 120)
    }

    window.addEventListener('scroll', schedulePersist, { passive: true })
    window.addEventListener('pagehide', persist)
    window.addEventListener('beforeunload', persist)

    return () => {
      if (throttleTimer != null) {
        window.clearTimeout(throttleTimer)
      }
      window.removeEventListener('scroll', schedulePersist)
      window.removeEventListener('pagehide', persist)
      window.removeEventListener('beforeunload', persist)
      persist()
    }
  }, [loc.pathname, loc.search, loc.hash])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (EXCLUDED_SCROLL_RESTORE_ROUTE_RE.test(loc.pathname)) return

    const previousPathname = previousPathnameRef.current
    const isFirstEntry = previousPathname == null
    const isPathnameChanged = previousPathname !== loc.pathname
    previousPathnameRef.current = loc.pathname

    if (!isFirstEntry && !isPathnameChanged) {
      return
    }

    const storageKey = buildRouteScrollStorageKey(loc.pathname, loc.search, loc.hash)
    const saved = readRouteScrollPosition(storageKey)
    const targetX = saved?.x ?? 0
    const targetY = saved?.y ?? 0

    if (restoreRafRef.current != null) {
      window.cancelAnimationFrame(restoreRafRef.current)
      restoreRafRef.current = null
    }

    restoreRafRef.current = window.requestAnimationFrame(() => {
      restoreRafRef.current = window.requestAnimationFrame(() => {
        window.scrollTo({
          left: targetX,
          top: targetY,
          behavior: 'auto'
        })
        restoreRafRef.current = null
      })
    })

    return () => {
      if (restoreRafRef.current != null) {
        window.cancelAnimationFrame(restoreRafRef.current)
        restoreRafRef.current = null
      }
    }
  }, [loc.pathname, loc.search, loc.hash, navigationType])

  return null
}

const GlobalPublicConfigMeta: React.FC = () => {
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    let cancelled = false

    const applySiteNameToMeta = (siteNameRaw: string) => {
      if (typeof document === 'undefined') return
      const siteName = String(siteNameRaw || '').trim()
      if (!siteName) return
      try { document.title = siteName } catch { void 0 }

      const setMetaByName = (name: string, content: string) => {
        try {
          const el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
          if (!el) return
          el.setAttribute('content', content)
        } catch { void 0 }
      }

      const setMetaByProperty = (property: string, content: string) => {
        try {
          const el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
          if (!el) return
          el.setAttribute('content', content)
        } catch { void 0 }
      }

      setMetaByName('application-name', siteName)
      setMetaByName('apple-mobile-web-app-title', siteName)
      setMetaByProperty('og:title', siteName)
      setMetaByProperty('twitter:title', siteName)
    }

    const load = async () => {
      try {
        const res = await request.get('/api/auth/public-config', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (cancelled) return
        if (res.data?.success) {
          const sn = res.data?.data?.site_name
          if (sn != null) applySiteNameToMeta(String(sn))
        }
      } catch {
        void 0
      }
    }

    const onCfg = () => { void load() }
    void load()
    window.addEventListener('ebookhub:config-changed', onCfg as EventListener)
    return () => {
      cancelled = true
      window.removeEventListener('ebookhub:config-changed', onCfg as EventListener)
    }
  }, [])
  return null
}

const PreventReadonlySelectIme: React.FC = () => {
  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const isCoarse = (() => {
      try {
        if (typeof window === 'undefined') return false
        const mq1 = window.matchMedia && window.matchMedia('(pointer: coarse)')
        const mq2 = window.matchMedia && window.matchMedia('(hover: none)')
        return Boolean(mq1?.matches || mq2?.matches)
      } catch {
        return false
      }
    })()
    if (!isCoarse) return

    const shouldDisableImeForInput = (input: HTMLInputElement) => {
      const host = input.closest('.ant-select')
      if (!host) return false
      if (host.classList.contains('ant-select-show-search')) return false
      if (host.classList.contains('ant-select-auto-complete')) return false
      if (host.classList.contains('ant-select-combobox')) return false
      if (host.getAttribute('aria-autocomplete')) return false
      if (input.getAttribute('aria-autocomplete')) return false
      if (input.getAttribute('role') === 'combobox') return false
      return true
    }

    const patchInputs = (root: ParentNode) => {
      try {
        const list = root.querySelectorAll('input.ant-select-selection-search-input')
        for (const el of Array.from(list)) {
          const input = el as HTMLInputElement
          if (!input) continue
          if (!shouldDisableImeForInput(input)) continue
          if (!input.readOnly) input.readOnly = true
          if (!input.hasAttribute('readonly')) input.setAttribute('readonly', '')
          if (input.getAttribute('inputmode') !== 'none') input.setAttribute('inputmode', 'none')
          if (input.getAttribute('autocomplete') !== 'off') input.setAttribute('autocomplete', 'off')
          if (input.getAttribute('autocorrect') !== 'off') input.setAttribute('autocorrect', 'off')
          if (input.getAttribute('autocapitalize') !== 'off') input.setAttribute('autocapitalize', 'off')
          if (input.getAttribute('spellcheck') !== 'false') input.setAttribute('spellcheck', 'false')
        }
      } catch { void 0 }
    }

    const rafIdRef = { current: 0 as number | 0 }
    const schedulePatch = (root: ParentNode) => {
      try { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) } catch { void 0 }
      rafIdRef.current = requestAnimationFrame(() => patchInputs(root))
    }

    const onFocusIn = (ev: Event) => {
      const t = ev.target as Element | null
      if (!t) return
      if (t instanceof HTMLInputElement && t.classList.contains('ant-select-selection-search-input')) {
        if (shouldDisableImeForInput(t)) schedulePatch(t.parentNode || document)
      }
    }

    const onPointerDown = (ev: Event) => {
      const t = ev.target as Element | null
      if (!t) return
      const host = t.closest('.ant-select')
      if (!host) return
      schedulePatch(host)
    }

    const onTouchStart = (ev: Event) => onPointerDown(ev)

    schedulePatch(document)
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('touchstart', onTouchStart, true)
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of Array.from(m.addedNodes || [])) {
          if (!n) continue
          if (n.nodeType !== Node.ELEMENT_NODE) continue
          schedulePatch(n as ParentNode)
        }
      }
    })
    try { obs.observe(document.body, { childList: true, subtree: true }) } catch { void 0 }

    return () => {
      try { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) } catch { void 0 }
      try { document.removeEventListener('focusin', onFocusIn, true) } catch { void 0 }
      try { document.removeEventListener('pointerdown', onPointerDown, true) } catch { void 0 }
      try { document.removeEventListener('touchstart', onTouchStart, true) } catch { void 0 }
      try { obs.disconnect() } catch { void 0 }
    }
  }, [])
  return null
}

const AppWithFontScale: React.FC = () => {
  const { fontSizePx } = useFontScale()

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#f59e0b', // Amber 500
          colorLink: '#d97706',    // Amber 600
          borderRadius: 12,        // 更圆润的边角
          wireframe: false,
          colorBgContainer: '#ffffff',
          colorBgLayout: '#fcfbf9', // 匹配 --color-bg-body
          fontSize: fontSizePx,
          fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
        },
        components: {
          Button: {
            colorPrimary: '#f59e0b',
            primaryShadow: '0 4px 10px rgba(245, 158, 11, 0.25)',
            algorithm: true,
            fontWeight: 500,
          },
          Layout: {
            headerBg: 'rgba(255, 255, 255, 0.8)', // 半透明 Header
            bodyBg: '#fcfbf9',
          },
          Menu: {
            colorBgContainer: 'transparent',
            itemSelectedBg: '#fffbeb', // Primary 50
            itemSelectedColor: '#d97706', // Primary 600
          },
          Card: {
            borderRadiusLG: 16,
            boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.03)', // 更柔和的阴影
          }
        }
      }}
    >
      <AntdApp>
        <AppErrorBoundary>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <GlobalPublicConfigMeta />
            <PreventReadonlySelectIme />
            <RouteTracker />
            <RouteScrollMemory />
            <div className="App">
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center h-screen">
                    <div className="text-center text-gray-600">页面加载中...</div>
                  </div>
                }
              >
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="import" element={<Import />} />
                    <Route path="search" element={<Search />} />
                  <Route path="tts" element={<TextToSpeech />} />
                    <Route path="favorites" element={<Favorites />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="help" element={<Help />} />
                    <Route path="reader/:id" element={<Reader />} />
                    <Route path="admin/*" element={<Admin />} />
                  </Route>
                </Routes>
              </React.Suspense>
              <Toaster position="top-right" />
            </div>
          </Router>
        </AppErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  )
}

function App() {
  return (
    <FontScaleProvider>
      <AppWithFontScale />
    </FontScaleProvider>
  )
}

export default App
